(() => {
  if (customElements.get('promotion-countdown')) return;

  // Parse a fixed instant; never use the visitor's local timezone or restart an expired offer.
  function parseDate(value, fallbackOffset) {
    const match = String(value || '').trim().match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})?$/);
    if (!match) return NaN;
    const [,year,month,day,hour,minute,second = '00',explicitOffset] = match;
    const parts = [year,month,day,hour,minute,second].map(Number);
    if (parts[0] < 1000 || parts[1] < 1 || parts[1] > 12 || parts[2] < 1 || parts[3] > 23 || parts[4] > 59 || parts[5] > 59) return NaN;
    const offset = (explicitOffset || fallbackOffset || '').trim();
    if (!/^(Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/.test(offset)) return NaN;
    if (offset !== 'Z' && Number(offset.slice(1,3)) === 14 && offset.slice(4) !== '00') return NaN;
    const local = new Date(Date.UTC(...[parts[0],parts[1]-1,...parts.slice(2)]));
    if (local.getUTCMonth() !== parts[1]-1 || local.getUTCDate() !== parts[2]) return NaN;
    const delta = offset === 'Z' ? 0 : (Number(offset.slice(1,3))*60+Number(offset.slice(4)))*(offset[0] === '-' ? -1 : 1);
    return local.getTime()-delta*60000;
  }

  class PromotionCountdown extends HTMLElement {
    connectedCallback() {
      // A dynamically upgraded element may be connected before its children finish parsing.
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.panel = this.querySelector('[data-pc-panel]');
      this.notice = this.querySelector('[data-pc-notice]');
      if (!this.panel) return;
      this.start = this.dataset.start?.trim() ? parseDate(this.dataset.start, this.dataset.offset) : -Infinity;
      this.end = parseDate(this.dataset.end, this.dataset.offset);
      this.pending = Boolean(this.pending);
      this.stale = false;
      this.lastRefresh = Date.now();
      this.refreshMs = Math.max(0, Number(this.dataset.refresh) || 0)*1000;
      const owner = this.closest('product-info');
      if (this.dataset.selectionConfirmed !== 'true' && owner?.dataset.disableSelectedVariantDefault === 'true' && !new URLSearchParams(location.search).has('variant')) this.pending = true;
      this.onVisibility = () => {
        if (document.visibilityState === 'visible') {
          this.render();
          this.refreshInventory();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.interval = setInterval(() => {
        if (document.visibilityState === 'hidden') return;
        this.render();
        this.refreshInventory();
      },1000);
      this.render();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      clearInterval(this.interval);
      this.request?.abort();
      document.removeEventListener('visibilitychange',this.onVisibility);
      this.initialized = false;
    }

    setPending() {
      this.pending = true;
      this.request?.abort();
      this.hidden = true;
    }

    render() {
      if (!this.panel) return;
      const now = Date.now();
      let reason = '';
      if (this.pending) reason = 'Select an available variant. A variant update may still be loading.';
      else if (!Number.isFinite(this.end) || Number.isNaN(this.start) || this.start >= this.end) reason = 'Set a valid end date, optional earlier start date, and UTC offset in Promotion Countdown.';
      else if (this.dataset.enabled !== 'true') reason = 'Hidden: set this variant’s custom.show_countdown metafield to true to enable the promotion.';
      else if (this.dataset.available !== 'true') reason = 'Hidden: the selected variant is unavailable or sold out.';
      else if (now < this.start) reason = 'Hidden: the promotion has not started yet.';
      else if (now >= this.end && this.dataset.expiry !== 'message') reason = 'Hidden: the promotion has ended.';
      const editor = Boolean(window.Shopify?.designMode);
      this.hidden = Boolean(reason) && !editor;
      this.panel.hidden = Boolean(reason);
      this.notice.hidden = !reason || !editor;
      if (editor && reason) this.notice.textContent = `Editor only — ${reason}`;
      if (reason) return;
      const ended = now >= this.end;
      this.querySelector('[data-pc-active]').hidden = ended;
      this.querySelector('[data-pc-ended]').hidden = !ended;
      if (ended) return;

      const seconds = Math.max(0, Math.ceil((this.end-now)/1000));
      const values = {days:Math.floor(seconds/86400),hours:Math.floor(seconds/3600)%24,minutes:Math.floor(seconds/60)%60,seconds:seconds%60};
      Object.entries(values).forEach(([unit,value]) => {
        const target = this.querySelector(`[data-pc-${unit}]`);
        const text = String(value).padStart(2,'0');
        if (target && target.textContent !== text) target.textContent = text;
      });
      this.renderStock();
    }

    renderStock() {
      const stock = this.querySelector('[data-pc-stock]');
      const progressWrap = this.querySelector('[data-pc-progress-wrap]');
      if (progressWrap) progressWrap.hidden = true;
      const quantity = Number(this.dataset.quantity);
      const tracked = this.dataset.tracked === 'true';
      // Do not claim a quantity when tracking is disabled, data is invalid, or a refresh failed.
      stock.hidden = this.stale || !tracked || !Number.isFinite(quantity);
      if (stock.hidden) return;
      let message = this.dataset.normalText;
      if (quantity <= 0) {
        if (this.dataset.policy !== 'continue') { stock.hidden = true; return; }
        message = this.dataset.backorderText;
      } else if (quantity <= Number(this.dataset.threshold)) {
        message = this.dataset.lowText.split('{count}').join(String(quantity));
      }
      const text = this.querySelector('[data-pc-stock-text]');
      if (text.textContent !== message) text.textContent = message;
      const reference = Number(this.dataset.reference);
      if (progressWrap && quantity > 0 && reference > 0 && quantity <= reference) {
        progressWrap.hidden = false;
        const percentage = quantity/reference*100;
        const progress = this.querySelector('[data-pc-progress]');
        progress.setAttribute('aria-valuenow',String(Math.round(percentage)));
        progress.setAttribute('aria-valuetext',`${quantity} remaining; reference quantity ${reference}`);
        this.querySelector('[data-pc-bar]').style.width = `${percentage}%`;
      }
    }

    async refreshInventory() {
      if (!this.refreshMs || this.pending || this.request || !this.isConnected || !Number.isFinite(this.end) || Date.now() >= this.end || Date.now()-this.lastRefresh < this.refreshMs) return;
      const variantId = this.dataset.variantId;
      if (!variantId) return;
      this.lastRefresh = Date.now();
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(),15000);
      try {
        const url = new URL(this.dataset.productUrl, location.origin);
        url.searchParams.set('variant',variantId);
        url.searchParams.set('section_id',this.dataset.sectionId);
        const response = await fetch(url, {signal:controller.signal,cache:'no-store'});
        if (!response.ok) throw new Error('Inventory refresh failed');
        const html = new DOMParser().parseFromString(await response.text(),'text/html');
        const source = Array.from(html.querySelectorAll('promotion-countdown')).find(item => item.dataset.blockId === this.dataset.blockId && item.dataset.productId === this.dataset.productId && item.dataset.variantId === variantId);
        if (!source) throw new Error('No matching countdown data');
        if (!this.isConnected || this.pending || this.dataset.variantId !== variantId) return;
        ['enabled','available','tracked','policy','quantity'].forEach(key => {this.dataset[key] = source.dataset[key];});
        this.stale = false;
      } catch (error) {
        if (this.isConnected && !this.pending) this.stale = true;
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) this.request = null;
        if (this.isConnected && !this.pending) this.render();
      }
    }
  }
  customElements.define('promotion-countdown',PromotionCountdown);
})();
